import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { gaps } from "../../../primitives/spacing";
import { AuthFormFrame } from "../AuthLayout";
import { ROUTES } from "../../../../route_paths";

interface LoginFormProps {
  title: string;
  subtitle: string;
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  loading: boolean;
  error: string | null;
  onSubmit: (e: React.FormEvent) => Promise<void>;
}

/** Shared email/password login form, meant to sit inside `AuthLayout`. Forgot-password always links to the top-level /forgot-password page. */
export function LoginForm({
  title,
  subtitle,
  email,
  setEmail,
  password,
  setPassword,
  loading,
  error,
  onSubmit,
}: LoginFormProps) {
  const { t } = useTranslation();

  return (
    <AuthFormFrame
      title={title}
      subtitle={subtitle}
      footer={
        <Button component={Link} to={ROUTES.forgotPassword.path()} variant="text" size="small">
          {t("auth.forgotPassword")}
        </Button>
      }
    >
      {error && <Alert severity="error">{error}</Alert>}
      <Stack component="form" onSubmit={onSubmit} gap={gaps.base}>
        <TextField
          label={t("auth.email")}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          fullWidth
          autoFocus
          autoComplete="email"
          size="small"
        />
        <TextField
          label={t("auth.password")}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          fullWidth
          autoComplete="current-password"
          size="small"
        />
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading || !email.trim() || !password.trim()}
        >
          {loading ? t("auth.signingIn") : t("auth.signIn")}
        </Button>
      </Stack>
    </AuthFormFrame>
  );
}
